import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Notifications as NotificationsIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ActionNotification } from '../../types/actions';
import { actionApi } from '../../utils/actionApi';

const PAGE_SIZE = 20;

const mergeUnique = (current: ActionNotification[], incoming: ActionNotification[]): ActionNotification[] => {
  const byId = new Map(current.map(item => [item.id, item]));
  incoming.forEach(item => byId.set(item.id, item));
  return Array.from(byId.values());
};

const NotificationCentre: React.FC = () => {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(true);
  const [items, setItems] = useState<ActionNotification[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const requestSequence = useRef(0);
  const requestController = useRef<AbortController | null>(null);

  const open = Boolean(anchor);
  const unreadCount = items.filter(item => item.readAt === null).length;

  const load = async (reset: boolean, cursor?: string) => {
    requestController.current?.abort();
    const controller = new AbortController();
    requestController.current = controller;
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError(false);
    try {
      const page = await actionApi.notifications(
        { unreadOnly, limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        controller.signal
      );
      if (sequence !== requestSequence.current) return;
      setItems(previous => reset ? mergeUnique([], page.items) : mergeUnique(previous, page.items));
      setNextCursor(page.nextCursor);
    } catch (requestError) {
      if (sequence === requestSequence.current && !actionApi.isCanceled(requestError)) setError(true);
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => () => requestController.current?.abort(), []);

  useEffect(() => {
    if (open) void load(true);
    // A filter change owns a fresh request. `load` deliberately remains local to avoid
    // recreating requests for unrelated render changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, unreadOnly]);

  const closePopover = () => {
    requestController.current?.abort();
    requestController.current = null;
    requestSequence.current += 1;
    setLoading(false);
    setAnchor(null);
  };

  const handleItemClick = async (item: ActionNotification) => {
    if (item.readAt === null) {
      const optimisticReadAt = new Date().toISOString();
      setItems(previous => previous.map(entry => entry.id === item.id
        ? { ...entry, readAt: optimisticReadAt }
        : entry));
      try {
        await actionApi.markNotificationRead(item.id);
        if (unreadOnly) setItems(previous => previous.filter(entry => entry.id !== item.id));
      } catch (readError) {
        if (actionApi.isNotFound(readError)) {
          // Authorization may have been revoked after this page was loaded. Do not
          // restore or retain content the caller can no longer read.
          setItems(previous => previous.filter(entry => entry.id !== item.id));
        } else {
          setItems(previous => previous.map(entry => entry.id === item.id
            ? { ...entry, readAt: null }
            : entry));
        }
      }
    }
    closePopover();
    navigate(`/actions/${encodeURIComponent(item.actionId)}`);
  };

  return (
    <>
      <IconButton
        color="inherit"
        aria-label={unreadCount > 0 ? 'Notifications, unread items loaded' : 'Notifications'}
        onClick={event => setAnchor(event.currentTarget)}
      >
        <Badge variant="dot" invisible={unreadCount === 0} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: { xs: 'calc(100vw - 24px)', sm: 400 }, maxHeight: '70vh' } } }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6">Notifications</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={unreadOnly ? 'unread' : 'all'}
              onChange={(_event, value: 'unread' | 'all' | null) => {
                if (value !== null) setUnreadOnly(value === 'unread');
              }}
              aria-label="Notification filter"
            >
              <ToggleButton value="unread">Unread</ToggleButton>
              <ToggleButton value="all">All</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {error && <Alert severity="error" sx={{ mt: 2 }}>Notifications are temporarily unavailable.</Alert>}
          {!loading && !error && items.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              {unreadOnly ? 'No unread notifications.' : 'No notifications.'}
            </Typography>
          )}
          <List aria-label="Action notifications" disablePadding sx={{ mt: 1 }}>
            {items.map(item => (
              <ListItemButton
                key={item.id}
                onClick={() => void handleItemClick(item)}
                sx={{ alignItems: 'flex-start', opacity: item.readAt === null ? 1 : 0.72 }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={
                    <>
                      <Typography component="span" variant="body2" color="text.primary">
                        {item.body}
                      </Typography>
                      <Typography component="span" variant="caption" display="block" color="text.secondary">
                        {new Date(item.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
          {loading && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} aria-label="Loading notifications" /></Box>}
          {!loading && nextCursor && (
            <Button fullWidth onClick={() => void load(false, nextCursor)}>Load more</Button>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationCentre;
